import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, UNITS } from "@/lib/api";
import { numStr, parseNum } from "@/lib/format";
import { Button } from "@/components/kit";
import { ingredientCalc } from "@/lib/recipeCalc";

const needsFix = (r, src, unit) => (r.ingredients || []).some((i) => i.product_id === src.id && ingredientCalc({ ...i, qty_gross: 1, loss_pct: 0 }, { ...src, unit }).incompatible);

function AffectedList({ list, ingKey, onClose }) {
  return (
    <div className="flex flex-col gap-2" data-testid={`ingredient-affected-${ingKey}`}>
      <div className="label flex items-center gap-1.5" style={{ color: "var(--danger)" }}><AlertTriangle size={15} /> Revise {list.length === 1 ? "esta ficha que também usa" : `estas ${list.length} fichas que também usam`} o insumo</div>
      <div className="caption">A unidade mudou. Abra cada ficha e ajuste a quantidade e a unidade do insumo.</div>
      <div className="flex flex-col divide rounded-lg" style={{ background: "var(--surface)" }}>
        {list.map((r) => (
          <a key={r.id} href={`/recipes/${r.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm hover:underline" data-testid={`affected-recipe-link-${r.id}`}>
            <span className="flex-1 truncate">{r.name}</span><span className="caption">Corrigir</span><ExternalLink size={13} />
          </a>
        ))}
      </div>
      <Button variant="ghost" size="sm" className="self-end" onClick={onClose} data-testid={`ingredient-affected-close-${ingKey}`}>Fechar</Button>
    </div>
  );
}

export function ProductQuickEdit({ src, ingKey, onSaved, onClose }) {
  const qc = useQueryClient();
  const { id: recipeId } = useParams();
  const [unit, setUnit] = useState(src.unit);
  const [cost, setCost] = useState(numStr(src.unit_cost));
  const [affected, setAffected] = useState(null);
  const save = useMutation({
    mutationFn: () => api(`/products/${src.id}/cost-unit`, { method: "PATCH", body: { unit, unit_cost: parseNum(cost) } }),
    onSuccess: async (p) => {
      await qc.invalidateQueries({ queryKey: ["products"] });
      await qc.invalidateQueries({ queryKey: ["recipes"] });
      onSaved(p);
      toast.success("Cadastro do insumo atualizado");
      if (p.unit === src.unit) return onClose();
      const recipes = await qc.fetchQuery({ queryKey: ["recipes"], queryFn: () => api("/recipes") });
      const list = recipes.filter((r) => r.id !== recipeId && needsFix(r, src, p.unit));
      if (!list.length) return onClose();
      setAffected(list);
    },
    onError: (e) => toast.error(e.message),
  });
  return (
    <div className="rounded-xl p-3 flex flex-col gap-2" style={{ background: "var(--surface-alt)" }} data-testid={`ingredient-edit-panel-${ingKey}`}>
      {affected ? <AffectedList list={affected} ingKey={ingKey} onClose={onClose} /> : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1"><span className="caption">Unidade do produto</span>
              <select className="input input-sm" value={unit} onChange={(e) => setUnit(e.target.value)} data-testid={`ingredient-edit-unit-${ingKey}`}>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</select>
            </label>
            <label className="flex flex-col gap-1"><span className="caption">Custo por {unit} (R$)</span>
              <input className="input input-sm tnum text-right" inputMode="decimal" value={cost} onFocus={(e) => e.target.select()} onChange={(e) => setCost(e.target.value)} data-testid={`ingredient-edit-cost-${ingKey}`} />
            </label>
          </div>
          <div className="caption">Altera o cadastro do produto · vale para todas as fichas e conferências abertas.</div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} data-testid={`ingredient-edit-cancel-${ingKey}`}>Cancelar</Button>
            <Button size="sm" loading={save.isPending} onClick={() => save.mutate()} data-testid={`ingredient-edit-save-${ingKey}`}>Salvar no cadastro</Button>
          </div>
        </>
      )}
    </div>
  );
}
